/**
 * Search + filter bar for the library.
 *
 * The accepted-extension list comes from `GET /assets/config/`, never from a
 * hardcoded array here — the server's `imagetypes.FORMATS` is the only source
 * of truth for what it will take, and a stale copy in the UI shows filters for
 * formats that no longer exist (or hides ones that do).
 */

import { AdjustmentsHorizontalIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Button, Input, Select } from '@/components/common';
import { cn } from '@/utils';
import type { AssetConfig } from '@/services/assets';

import {
  EMPTY_FILTERS,
  ORDERING_OPTIONS,
  activeFilterCount,
  type AssetFilterState,
} from './assetFilterState';

interface AssetFiltersProps {
  search: string;
  onSearchChange: (value: string) => void;
  filters: AssetFilterState;
  onFiltersChange: (filters: AssetFilterState) => void;
  config?: AssetConfig | null;
  showAdvanced: boolean;
  onToggleAdvanced: () => void;
  className?: string;
  /** Hide the ordering + advanced controls (the picker wants a slim bar). */
  compact?: boolean;
}

export function AssetFilters({
  search,
  onSearchChange,
  filters,
  onFiltersChange,
  config,
  showAdvanced,
  onToggleAdvanced,
  className,
  compact = false,
}: AssetFiltersProps) {
  const set = <K extends keyof AssetFilterState>(key: K, value: AssetFilterState[K]) =>
    onFiltersChange({ ...filters, [key]: value });

  const extensionOptions = [
    { value: '', label: 'Any format' },
    ...(config?.accepted_extensions || []).map((ext) => ({ value: ext, label: ext.toUpperCase() })),
  ];

  const count = activeFilterCount(filters);

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[200px] flex-1">
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search file names, titles, alt text, captions…"
            leftIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
            className="dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            aria-label="Search the media library"
          />
        </div>

        <Select
          options={[
            { value: '', label: 'All types' },
            { value: 'raster', label: 'Photos & logos' },
            { value: 'vector', label: 'Vectors (SVG)' },
          ]}
          value={filters.kind}
          onChange={(event) => set('kind', event.target.value as AssetFilterState['kind'])}
          className="w-auto dark:border-gray-600 dark:bg-gray-900 dark:text-white"
          aria-label="Filter by image type"
        />

        {!compact && (
          <Select
            options={ORDERING_OPTIONS}
            value={filters.ordering}
            onChange={(event) => set('ordering', event.target.value)}
            className="w-auto dark:border-gray-600 dark:bg-gray-900 dark:text-white"
            aria-label="Sort order"
          />
        )}

        <Button
          variant={showAdvanced || count ? 'secondary' : 'outline'}
          size="md"
          leftIcon={<AdjustmentsHorizontalIcon className="h-4 w-4" />}
          onClick={onToggleAdvanced}
        >
          Filters{count ? ` (${count})` : ''}
        </Button>

        {count > 0 && (
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<XMarkIcon className="h-4 w-4" />}
            onClick={() => onFiltersChange({ ...EMPTY_FILTERS, ordering: filters.ordering })}
          >
            Clear
          </Button>
        )}
      </div>

      {showAdvanced && (
        <div className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-900/40 sm:grid-cols-2 lg:grid-cols-4">
          <FilterField label="Format">
            <Select
              options={extensionOptions}
              value={filters.extension}
              onChange={(event) => set('extension', event.target.value)}
              className="dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </FilterField>

          <FilterField label="Usage">
            <Select
              options={[
                { value: '', label: 'Used and unused' },
                { value: 'used', label: 'In use somewhere' },
                { value: 'unused', label: 'Not used anywhere' },
              ]}
              value={filters.usage}
              onChange={(event) => set('usage', event.target.value as AssetFilterState['usage'])}
              className="dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </FilterField>

          <FilterField label="Tag">
            <Input
              value={filters.tag}
              onChange={(event) => set('tag', event.target.value)}
              placeholder="e.g. logo"
              className="dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </FilterField>

          <FilterField label="Minimum width (px)">
            <Input
              type="number"
              min={1}
              value={filters.minWidth}
              onChange={(event) => set('minWidth', event.target.value)}
              placeholder="e.g. 1200"
              className="dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </FilterField>

          <FilterField label="Uploaded on or after">
            <Input
              type="date"
              value={filters.createdAfter}
              onChange={(event) => set('createdAfter', event.target.value)}
              className="dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            />
          </FilterField>
        </div>
      )}
    </div>
  );
}

function FilterField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-gray-600 dark:text-gray-300">{label}</label>
      {children}
    </div>
  );
}
